import React, { useState, useEffect } from 'react';
import { Modal, Table, InputNumber, Input, Button, Alert, Space } from 'antd';
import { rulesAPI } from '../services/api';
import { message } from 'antd';

export default function RulesEditor({ isOpen, onClose }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) fetchRules();
  }, [isOpen]);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await rulesAPI.getDesignSpeeds();
      setRules(res.data);
      setError('');
    } catch {
      setRules(getDefaultRules());
      setError('Using default rules (database not connected)');
    }
    setLoading(false);
  };

  const getDefaultRules = () => [
    { id: 1, speed_min: 0, speed_max: 30, lowercase_x_height_mm: 70, uppercase_x_height_mm: 100, description: 'Urban/Low Speed' },
    { id: 2, speed_min: 31, speed_max: 50, lowercase_x_height_mm: 100, uppercase_x_height_mm: 140, description: 'Urban Roads' },
    { id: 3, speed_min: 51, speed_max: 65, lowercase_x_height_mm: 125, uppercase_x_height_mm: 175, description: 'State Highways' },
    { id: 4, speed_min: 66, speed_max: 80, lowercase_x_height_mm: 175, uppercase_x_height_mm: 245, description: 'National Highways' },
    { id: 5, speed_min: 81, speed_max: 100, lowercase_x_height_mm: 215, uppercase_x_height_mm: 300, description: 'Expressways' },
    { id: 6, speed_min: 101, speed_max: 120, lowercase_x_height_mm: 305, uppercase_x_height_mm: 425, description: 'High-Speed Corridors' },
    { id: 7, speed_min: 121, speed_max: 150, lowercase_x_height_mm: 340, uppercase_x_height_mm: 475, description: 'Access Controlled' },
  ];

  const updateRule = (index, field, value) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], [field]: field === 'description' ? value : parseInt(value) || 0 };
    setRules(updated);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      for (const rule of rules) {
        if (rule.id) {
          await rulesAPI.updateDesignSpeed(rule.id, rule);
        }
      }
      message.success('Rules saved successfully');
      onClose();
    } catch {
      setError('Failed to save. Changes kept locally.');
      message.error('Failed to save rules');
    }
    setLoading(false);
  };

  const columns = [
    {
      title: 'Speed Min',
      dataIndex: 'speed_min',
      key: 'speed_min',
      render: (value, record, index) => (
        <InputNumber
          value={value}
          onChange={(val) => updateRule(index, 'speed_min', val)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Speed Max',
      dataIndex: 'speed_max',
      key: 'speed_max',
      render: (value, record, index) => (
        <InputNumber
          value={value}
          onChange={(val) => updateRule(index, 'speed_max', val)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Lowercase x-Height (mm)',
      dataIndex: 'lowercase_x_height_mm',
      key: 'lowercase_x_height_mm',
      render: (value, record, index) => (
        <InputNumber
          value={value}
          onChange={(val) => updateRule(index, 'lowercase_x_height_mm', val)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Uppercase x-Height (mm)',
      dataIndex: 'uppercase_x_height_mm',
      key: 'uppercase_x_height_mm',
      render: (value, record, index) => (
        <InputNumber
          value={value}
          onChange={(val) => updateRule(index, 'uppercase_x_height_mm', val)}
          min={0}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value, record, index) => (
        <Input
          value={value}
          onChange={(e) => updateRule(index, 'description', e.target.value)}
        />
      ),
    },
  ];

  return (
    <Modal
      title="Design Speed Rules (Table 8.3)"
      open={isOpen}
      onCancel={onClose}
      onOk={handleSave}
      confirmLoading={loading}
      okText="Save Rules"
      width={900}
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        {error && (
          <Alert
            message={error}
            type="warning"
            showIcon
            closable
            onClose={() => setError('')}
          />
        )}
        <Table
          dataSource={rules.map((r, i) => ({ ...r, key: r.id || i }))}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ x: true }}
        />
      </Space>
    </Modal>
  );
}
